import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, 
  Card, 
  Button, 
  Tag, 
  Space,
  Modal,
  message,
  Row,
  Col,
  Statistic,
  DatePicker,
  Form,
  Select
} from 'antd';
import { 
  TransactionOutlined,
  DollarOutlined,
  FilterOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { agencyTransactionService } from '../../services/agencyTransactionService';
import { Transaction, TransactionFilter } from '../../types/transaction.types';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

const { RangePicker } = DatePicker;
const { Option } = Select;

const AgencyTransactions: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [filterForm] = Form.useForm();
  const [stats, setStats] = useState({
    totalRevenue: 0,
    monthlyRevenue: 0,
    successfulTransactions: 0,
    pendingTransactions: 0
  });
  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await agencyTransactionService.getAll();
      const sortedData = response.data.sort(
        (a, b) => moment(b.transactionDateTime).valueOf() - moment(a.transactionDateTime).valueOf()
      );
      setTransactions(sortedData);
      setFilteredTransactions(sortedData);
      calculateStats(sortedData);
    } catch (error) {
      message.error(t('agency.transactions.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const calculateStats = (data) => {
    const currentMonth = moment().format('MM/YYYY');
    const stats = data.reduce((acc, curr) => ({
      totalRevenue: acc.totalRevenue + curr.amount,
      monthlyRevenue: acc.monthlyRevenue + 
        (moment(curr.transactionDateTime).format('MM/YYYY') === currentMonth ? curr.amount : 0),
      successfulTransactions: acc.successfulTransactions + 
        (curr.status === 'COMPLETED' ? 1 : 0),
      pendingTransactions: acc.pendingTransactions + 
        (curr.status === 'PENDING' ? 1 : 0)
    }), { totalRevenue: 0, monthlyRevenue: 0, successfulTransactions: 0, pendingTransactions: 0 });
    
    setStats(stats);
  };
  const handleFilter = (values: any) => {
    let data = [...transactions];

    if (values.dateRange) {
      const start = moment(values.dateRange[0])
        .startOf('day')
        .format('YYYY-MM-DD HH:mm:ss');

      const end = moment(values.dateRange[1])
        .endOf('day')
        .format('YYYY-MM-DD HH:mm:ss');

      data = data.filter(tx =>
        moment(tx.transactionDateTime).isBetween(start, end, undefined, '[]')
      );
    }
    if (values.type) {
      data = data.filter(tx => tx.transactionType === values.type);
    }

    data.sort((a, b) => moment(b.transactionDateTime).valueOf() - moment(a.transactionDateTime).valueOf());

    setFilteredTransactions(data);
    calculateStats(data);
  };

  const exportTransactions = async () => {
    try {
      await agencyTransactionService.exportToExcel();
      message.success(t('agency.transactions.exportSuccess'));
    } catch (error) {
      message.error(t('agency.transactions.exportError'));
    }
  };

  const columns = [
    {
      title: t('common.id'),
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: t('common.type'),
      dataIndex: 'transactionType',
      key: 'transactionType',
      render: (transactionType) => (
        <Tag color={transactionType === 'DEBIT' ? 'green' : 'blue'}>
          {transactionType}
        </Tag>
      ),
    },
    {
      title: t('common.amount'),
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => (
        <Space>
          <DollarOutlined />
          {`${amount} €`}
        </Space>
      ),
    },
    {
      title: t('common.date'),
      dataIndex: 'transactionDateTime',
      key: 'transactionDateTime',
      render: (transactionDateTime) => moment(transactionDateTime).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => {
            setSelectedTransaction(record);
            setDetailsVisible(true);
          }}
        >
          {t('agency.transactions.details')}
        </Button>
      ),
    },
  ];

  return (
    <>
      <Row gutter={[24, 24]}>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('agency.transactions.totalRevenue')}
              value={stats.totalRevenue}
              prefix="€"
              precision={2}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('agency.transactions.monthlyRevenue')}
              value={stats.monthlyRevenue}
              prefix="€"
              precision={2}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        {/*<Col span={6}>
          <Card>
            <Statistic
              title="Transactions Réussies"
              value={stats.successfulTransactions}
              prefix={<TransactionOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Transactions en Attente"
              value={stats.pendingTransactions}
              prefix={<TransactionOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>*/}
      </Row>

      <Card 
        title={t('agency.transactions.title')}
        extra={
          <Space>
            <Button 
              icon={<DownloadOutlined />}
              onClick={exportTransactions}
            >
              {t('agency.transactions.export')}
            </Button>
          </Space>
        }
      >
        <Form
          form={filterForm}
          layout="inline"
          onFinish={handleFilter}
          style={{ marginBottom: 24 }}
        >
          <Form.Item name="dateRange">
            <RangePicker />
          </Form.Item>
          <Form.Item name="type">
            <Select placeholder={t('agency.transactions.typeFilter')} style={{ width: 120 }}>
              <Option value="DEBIT">DEBIT</Option>
              <Option value="CREDIT">CREDIT</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit"
              icon={<FilterOutlined />}
            >
              {t('agency.transactions.applyFilter')}
            </Button>
          </Form.Item>
          <Form.Item>
            <Button
              onClick={() => {
                filterForm.resetFields();
                setFilteredTransactions(transactions);
                calculateStats(transactions);
              }}
            >
              {t('agency.transactions.resetFilter')}
            </Button>
            </Form.Item>
        </Form>

        <Table 
          columns={columns}
          dataSource={filteredTransactions}
          loading={loading}
          rowKey="id"
        />
      </Card>

      <Modal
        title={t('agency.transactions.detailsTitle')}
        visible={detailsVisible}
        onCancel={() => {
          setDetailsVisible(false);
          setSelectedTransaction(null);
        }}
        footer={null}
        width={600}
      >
        {selectedTransaction && (
          <Card>
            <p><strong>{t('common.id')}:</strong> {selectedTransaction.id}</p>
            <p><strong>{t('common.type')}:</strong> {selectedTransaction.transactionType}</p>
            <p><strong>{t('common.amount')}:</strong> {selectedTransaction.amount} €</p>
            <p><strong>{t('common.date')}:</strong> {moment(selectedTransaction.transactionDateTime).format('DD/MM/YYYY HH:mm')}</p>
            <p><strong>{t('common.description')}:</strong> {selectedTransaction.description}</p>
            {selectedTransaction.subscriberId && (
              <p><strong>{t('agency.transactions.subscriberId')}:</strong> {selectedTransaction.subscriberId}</p>
            )}
          </Card>
        )}
      </Modal>
    </>
  );
};

export default AgencyTransactions;
